/**
 * ContentSectionHeader
 * --------------------
 * WHAT: Header row rendered at the top of each section / page. Shows the
 *       title plus optional action icons (edit / delete / drag handle) and
 *       an optional info slot (tutorial link / diagram deck).
 *
 * WHY: Headers differ between modes:
 *   - Readonly: big H2-styled title, no controls.
 *   - Edit + editable (custom section): title + edit icon + delete icon + drag handle.
 *   - Edit + non-editable (built-in section): title + drag handle only.
 *   - 'architecture' section gets an extra DiagramGuidelinesDeck in its info slot.
 *
 * STATE: mirrors props.title into local state to support live preview while
 *        the user is typing in the rename modal — BUT see BUG below, the
 *        local state is never written by this component, so it's redundant.
 */
import { TiptapEditorHTMLView } from '@amzn/awscat-editor';
import { Box, Button, Header, SpaceBetween } from '@amzn/awsui-components-core';
import React from 'react';
import constants from '../../common/constants';
import { MoveVerticallyHandle } from '../common/MoveVerticallyHandle';
import { TiptapOutputType } from '@amzn/awscat-editor';
import { getText } from '@amzn/awscat-editor';
import DiagramGuidelinesDeck from './DiagramGuidelinesDeck';

interface Props {
  readonly sectionId: string;
  readonly title: TiptapOutputType;
  readonly readonly: boolean;
  readonly isEditable: boolean; // Editale is true only for custom content section
  readonly isSortable?: boolean;
  readonly onSectionDelete?: (sectionId: string) => void;
  readonly onSectionEditClick?: (sectionId: string) => void;
  readonly headerInfo?: React.ReactNode; // Extra slot next to the title (tutorial link).
}

interface State {
  // BUG / DEAD STATE: this mirrors props.title but is only ever written in
  // componentDidUpdate. Since render() just reads it back, this is equivalent
  // to rendering props.title directly. The local state adds zero behavior and
  // introduces a one-render lag when props.title changes.
  title: TiptapOutputType;
}

class ContentSectionHeader extends React.Component<Props, State> {
  private static defaultProps = {
    isSortable: true,
  };

  // @ts-expect-error TS7006 - strict migration
  constructor(props) {
    super(props);
    this.state = {
      title: this.props.title,
    };
  }

  // Keep local title in sync when the section changes (or title is edited).
  // See BUG note on State.title — this whole lifecycle is unnecessary.
  public componentDidUpdate(prevProps: Props) {
    const { sectionId: prevSectionId, title: prevTitle } = prevProps;
    if (prevSectionId !== this.props.sectionId || prevTitle !== this.props.title) {
      const { title } = this.props;
      this.setState({
        title,
      });
    }
  }

  public render() {
    const { readonly } = this.props;
    const { title } = this.state;
    // getText extracts plain text from the TipTap doc (strips marks).
    // Then TiptapEditorHTMLView renders it back as HTML so mentions/links
    // remain clickable.
    //
    // DEAD CODE smell: getText + TiptapEditorHTMLView is an odd pairing —
    // getText strips formatting, then HTMLView tries to render it as HTML.
    // The net effect is "render the title as plain text through the HTML
    // view", which could be simplified to a plain <span>{getText(title)}</span>.
    const titleText = getText(title);
    const htmlTitle = <TiptapEditorHTMLView model={titleText} />;
    if (readonly) {
      // Big H2 title, no actions. Used on published pages.
      return (
        <Box display="inline-block" variant="h3" fontSize="heading-xl">
          <span className="awsui-content-section-header">{htmlTitle}</span>
        </Box>
      );
    }

    // Edit mode: CloudScape Header with right-aligned actions + optional info slot.
    return (
      <Header variant="h3" actions={this.renderHeaderActions()} info={this.props?.headerInfo}>
        {htmlTitle}
      </Header>
    );
  }

  /**
   * Compose the right-hand actions for the header.
   *
   * isEditable=true  -> edit + delete + (drag if sortable). Used for custom
   *                     Guide/Strategy sections that can be renamed/removed.
   * isEditable=false -> drag only. Used for built-in pattern sections (fixed
   *                     names) and the 'architecture' section gets DiagramGuidelinesDeck.
   *
   * BUG: when isEditable=true, .bind(this) is called on a POSSIBLY undefined
   *     `onSectionEditClick` — `undefined.bind(this)` throws. Happens if a
   *     caller sets isEditable=true but omits the onSectionEditClick prop.
   *     Should be: `isEditable && onSectionEditClick ? ... : null`.
   *
   * BUG: the edit "icon" is a raw inline <svg> with onClick, not a <Button>.
   *     That means it lacks keyboard focus, aria-label, and hover styles you
   *     get from CloudScape Button. The delete icon IS a Button — inconsistent.
   *     Replace with a Button variant="icon" during refactor.
   *
   * DEAD CODE: `editSection` is computed but only used inline — could inline directly.
   */
  private renderHeaderActions = () => {
    const { isEditable, isSortable } = this.props;
    // @ts-expect-error TS2532 - strict migration
    const editSection = isEditable ? this.props.onSectionEditClick.bind(this) : null;
    const sortDragHandler = isSortable ? (
      <Box variant="span" padding={{ right: 's' }}>
        <MoveVerticallyHandle />
      </Box>
    ) : null;
    // Section-specific extra: the Architecture section gets a "Diagram guidelines" popup.
    const diagramDeck = this.props.sectionId === 'architecture' ? <DiagramGuidelinesDeck /> : null;
    return isEditable ? (
      <SpaceBetween direction="horizontal" size="xxxs">
        <div className="edit-section-icon">
          {/* Raw SVG with onClick — see BUG about using a Button instead. */}
          <svg
            // @ts-expect-error TS2322 - strict migration
            onClick={editSection}
            width="20"
            height="20"
            viewBox="0 0 2048 2048"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path fill="#444" d={constants.EDIT_ICON_URI} />
          </svg>
        </div>
        <div className="delete-section-icon">
          <Button
            variant="icon"
            iconUrl={'/images/icon-delete.svg'}
            iconAlt="Delete section"
            ariaLabel="Delete section"
            onClick={this.handleSectionRemoved}
          />
        </div>
        {sortDragHandler}
      </SpaceBetween>
    ) : (
      // When not editable, show drag handle (preferred) OR diagram deck — not both.
      // This is why architecture sections don't show the drag handle; minor gotcha.
      sortDragHandler || diagramDeck
    );
  };

  /**
   * Delete confirmation. Same synchronous `confirm()` + hard-coded string
   * pattern as PatternContentTroubleshooting.deleteEntry.
   *
   * BUG: confirm text is hard-coded here instead of using constants.ts (other
   * delete flows use constants). Worth unifying when refactoring.
   */
  private handleSectionRemoved = () => {
    const { sectionId, onSectionDelete } = this.props;
    const confirmText = 'This operation cannot be undone. Please confirm the delete operation';
    if (confirm(confirmText) && onSectionDelete) {
      onSectionDelete(sectionId);
    }
  };
}

export default ContentSectionHeader;
